import { Check, ChevronDown, Copy } from 'lucide-react';
import { useEffect, useId, useState, type ReactNode, type Ref } from 'react';
import { Link } from 'react-router-dom';
import { Button, type ButtonVariant } from './Button';
import { cn } from './cn';

/*
  EmptyState — «тут пока пусто» и состояние вкладки целиком (R42): знак или значок в брендовом круге,
  заголовок, описание, действия. Единственный способ оформить пустоту (v1 §11.5).
  - frame="card" (по умолчанию) — пунктирная карточка внутри страницы: «Ничего не нашлось», «Пока пусто».
  - frame="page" — вкладка целиком (не найдено, нет доступа, не открылось, сбой): без карточки, по центру,
    заголовок h1 — на него ставят фокус, когда состояние пришло после перехода. По центру стоит всё, что выше
    раскрывашки подробностей: раскрытые, они растут вниз и ничего не сдвигают, пока им хватает места.
  Знак ui не рисует (ui не импортирует brand): медальон знака передаёт вызывающий в mark.
  status — строка шага под заголовком (место держит, пока пусто); details — раскрывашка «Подробности для
  поддержки» с кнопкой «Скопировать».
*/

export interface EmptyStateAction {
  label: string;
  to?: string;
  onClick?: () => void;
  /** Первое действие по умолчанию secondary, второе — ghost. */
  variant?: ButtonVariant;
  icon?: ReactNode;
  disabled?: boolean;
}

interface EmptyStateProps {
  /** Значок в брендовом круге — для пустоты внутри страницы. */
  icon?: ReactNode;
  /** Знак вместо значка — медальон StateMark (состояние вкладки целиком). */
  mark?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  /** Строка шага под заголовком; передана — место под неё держится, даже пока она пуста. */
  status?: ReactNode;
  /** Подробности для поддержки: код и текст ошибки, моноширинно, с «Скопировать». */
  details?: string;
  frame?: 'card' | 'page';
  headingRef?: Ref<HTMLHeadingElement>;
  className?: string;
}

export function EmptyState({
  icon,
  mark,
  title,
  description,
  action,
  secondaryAction,
  status,
  details,
  frame = 'card',
  headingRef,
  className,
}: EmptyStateProps) {
  const page = frame === 'page';
  return (
    <div
      data-slot="empty-state"
      data-frame={frame}
      className={cn(
        'flex flex-col items-center px-8 py-16 text-center',
        page ? 'min-h-full' : 'rounded-lg border border-dashed border-line bg-card',
        className,
      )}
    >
      {/* Центр — распорками сверху и снизу; нижнюю у подробностей даёт сама раскрывашка. basis-0, а не
          flex-1: высота колонки не задана (min-h-full), и основа 0% стала бы размером содержимого — раскрытые
          подробности снова сдвигали бы всё вверх. */}
      {page && <div aria-hidden className="grow basis-0" />}
      {mark ??
        (icon && (
          <span className="grid size-14 place-items-center rounded-full bg-brand-soft text-brand-fg [&_svg]:size-7">
            {icon}
          </span>
        ))}
      {page ? (
        <h1 ref={headingRef} tabIndex={-1} className="mt-6 type-heading text-fg focus:outline-none">
          {title}
        </h1>
      ) : (
        <h3 ref={headingRef} className="mt-4 text-base font-semibold text-fg">
          {title}
        </h3>
      )}
      {description && <p className="mt-2 max-w-md text-sm text-fg-muted">{description}</p>}
      {status !== undefined && <p className="mt-2 min-h-4 text-2xs text-fg-subtle tabular-nums">{status}</p>}
      {(action || secondaryAction) && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {action && <ActionButton action={action} variant="secondary" />}
          {secondaryAction && <ActionButton action={secondaryAction} variant="ghost" />}
        </div>
      )}
      {details ? <SupportDetails text={details} /> : page && <div aria-hidden className="grow basis-0" />}
    </div>
  );
}

function ActionButton({ action, variant }: { action: EmptyStateAction; variant: ButtonVariant }) {
  const button = (
    <Button
      variant={action.variant ?? variant}
      size="md"
      leftIcon={action.icon}
      disabled={action.disabled}
      onClick={action.to ? undefined : action.onClick}
    >
      {action.label}
    </Button>
  );
  return action.to ? (
    <Link to={action.to} viewTransition>
      {button}
    </Link>
  ) : (
    button
  );
}

/** Сколько держится «Скопировано», мс. */
const COPIED_MS = 1600;

/**
 * «Подробности для поддержки»: раскрывается по нажатию, текст копируется одной кнопкой. Та же раскрывашка —
 * у сцены падения интерфейса (RootCrash). Ставится последней в колонку (flex-col), центрированную распорками:
 * сама она — нижняя распорка, и раскрытый текст растёт в неё, а кнопка остаётся под указателем.
 */
export function SupportDetails({ text }: { text: string }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const t = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(t);
  }, [copied]);

  const copy = () => {
    try {
      navigator.clipboard.writeText(text).then(
        () => setCopied(true),
        () => undefined,
      );
    } catch {
      // Буфер обмена недоступен (небезопасный адрес): текст виден и выделяется руками.
    }
  };

  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className="group mt-6 inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs font-medium text-fg-muted transition-interactive hover:text-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        Подробности для поддержки
        <ChevronDown className="size-3.5 transition-transform duration-fast group-aria-expanded:rotate-180" />
      </button>
      <div className="w-full max-w-md grow basis-0">
        {open && (
          <div
            id={id}
            className="mt-2 flex w-full animate-fade-in items-start gap-2 rounded-md border border-line bg-muted p-3 text-left"
          >
            <pre className="min-w-0 flex-1 font-mono text-2xs leading-5 break-all whitespace-pre-wrap text-fg-muted">
              {text}
            </pre>
            <Button size="sm" variant="ghost" leftIcon={copied ? <Check /> : <Copy />} onClick={copy}>
              {copied ? 'Скопировано' : 'Скопировать'}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
